import React, { useState } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Box, Divider, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import {
    IInvitationType,
    useEmployeeQuery,
    useRemoveEmployeesMutation,
    useResendEmployeeInvitationMutation,
    useInvitationsCancelMutation,
    useInvitationsCreateMutation,
} from 'graphql/types';
import { Button } from 'common/components/buttons/Button';
import { ArrowIcon } from 'common/components/icons';
import { DetailBox } from 'common/components/DetailBox';
import { BasePage } from 'pages/BasePage';
import { DetailRow } from 'common/components/DetailRow';
import { ButtonLink } from 'common/components/buttons/ButtonLink';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { InvitationForm } from 'employee/components/InvitationForm';
import { getDisplayDate, getIntlPath } from 'common/utils';
import { useSnackbarContext } from 'common/context';

interface IProps {
    className?: string;
}

const BaseEmployeeDetailPage = ({ className }: IProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [selectedInvitationForResend, setSelectedInvitationForResend] =
        useState<IInvitationType['id'] | null>(null);
    const [
        selectedInvitationForCancellation,
        setSelectedInvitationForCancellation,
    ] = useState<IInvitationType['id'] | null>(null);
    const { id } = useParams<{ id: string }>();
    const { setSnackbarState } = useSnackbarContext();
    const { data, loading, refetch } = useEmployeeQuery({
        variables: {
            id,
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });
    const [removeEmployees] = useRemoveEmployeesMutation({
        onCompleted: () => {
            history.push(getIntlPath('invitations'));
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const [resendInvitation] = useResendEmployeeInvitationMutation({
        onCompleted: () => {
            setSnackbarState({
                message: t('messages.resentSuccess'),
                variant: 'success',
            });
            refetch();
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const [cancelInvitations] = useInvitationsCancelMutation({
        onCompleted: () => {
            setSnackbarState({
                message: t('messages.invitationCancelled'),
                variant: 'success',
            });
            refetch();
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const [createInvitations] = useInvitationsCreateMutation({
        onCompleted: () => {
            setSnackbarState({
                message: t('messages.invitationSent'),
                variant: 'success',
            });
            refetch();
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const handleRemove = () => {
        removeEmployees({
            variables: {
                idList: [id],
            },
        });
        setShowRemoveDialog(false);
    };

    const handleResendClick = () => {
        if (!selectedInvitationForResend) return;

        resendInvitation({
            variables: {
                id: selectedInvitationForResend,
            },
        });

        setSelectedInvitationForResend(null);
    };

    const handleCancelClick = () => {
        if (!selectedInvitationForCancellation) return;

        cancelInvitations({
            variables: {
                ids: [selectedInvitationForCancellation],
            },
        });

        setSelectedInvitationForCancellation(null);
    };

    const { employee } = data || {};

    const handleInvitationFormSubmit = (budgetIds: string[]) => {
        if (!employee) return;

        createInvitations({
            variables: {
                budgetIds,
                employeeId: employee.id,
            },
        });
    };

    return (
        <BasePage
            Button={
                <Button
                    className="backButton"
                    onClick={() => history.push(getIntlPath('invitations'))}
                >
                    <ArrowIcon className="arrowIcon" />
                    {t('employee.detail.backButton')}
                </Button>
            }
            className={className}
            pageTitle={t('employee.detail.pageTitle')}
        >
            <Box mb={5}>
                <Grid container spacing={5}>
                    <Grid item lg={6} xs={12}>
                        <DetailBox
                            className="personalDetails"
                            loading={loading}
                            title={t('employee.personalInformation')}
                        >
                            {employee && (
                                <>
                                    <DetailRow
                                        description={t('employee.detail.name')}
                                    >
                                        {`${employee.firstName} ${employee.lastNamePrefix}  ${employee.lastName}`}
                                    </DetailRow>
                                    <DetailRow
                                        description={t('formfields.email')}
                                    >
                                        {employee.email}
                                    </DetailRow>
                                    <DetailRow
                                        description={t(
                                            'formfields.personnelNumber'
                                        )}
                                    >
                                        {employee.personnelNumber}
                                    </DetailRow>
                                </>
                            )}
                            <Box mb={4} mt={8}>
                                <Divider />
                            </Box>
                            <ButtonLink
                                className="link"
                                icon={<ArrowForwardIcon />}
                                onClick={() => setShowRemoveDialog(true)}
                            >
                                {t('employee.remove')}
                            </ButtonLink>
                        </DetailBox>
                    </Grid>

                    <Grid item lg={6} xs={12}>
                        <InvitationForm
                            budgets={employee?.company.budgets}
                            onSubmit={handleInvitationFormSubmit}
                        />
                    </Grid>
                </Grid>
            </Box>

            <DetailBox
                description={t('employee.detail.appointmentsDescription')}
                loading={loading}
                title={t('employee.detail.appointmentInfo')}
            >
                {employee &&
                    employee.invitations.map((invitation) => (
                        <div key={invitation.id}>
                            <DetailRow
                                description={t('employee.detail.budget')}
                            >
                                {invitation.budget.name}
                            </DetailRow>
                            <DetailRow
                                description={t('employee.detail.invitationId')}
                            >
                                {invitation.externalId}
                            </DetailRow>
                            <DetailRow
                                description={t('employee.detail.sentAt')}
                            >
                                {getDisplayDate(invitation.lastSent)}
                            </DetailRow>
                            <DetailRow description={t('employee.status')}>
                                {invitation.statusDisplay}
                            </DetailRow>
                            <Box mt={4}>
                                {invitation.canSend && (
                                    <Box mb={2}>
                                        <ButtonLink
                                            className="link"
                                            icon={<ArrowForwardIcon />}
                                            onClick={() =>
                                                setSelectedInvitationForResend(
                                                    invitation.id
                                                )
                                            }
                                        >
                                            {t('employee.detail.resend')}
                                        </ButtonLink>
                                    </Box>
                                )}
                                <Box>
                                    <ButtonLink
                                        className="link"
                                        icon={<ArrowForwardIcon />}
                                        onClick={() =>
                                            setSelectedInvitationForCancellation(
                                                invitation.id
                                            )
                                        }
                                    >
                                        {t('employee.detail.cancel')}
                                    </ButtonLink>
                                </Box>
                            </Box>
                            <Box mb={4} mt={4}>
                                <Divider />
                            </Box>
                        </div>
                    ))}
            </DetailBox>
            {employee && (
                <>
                    <ConfirmDialog
                        cancelProps={{
                            onClick: () => setShowRemoveDialog(false),
                        }}
                        confirmProps={{
                            onClick: handleRemove,
                        }}
                        open={showRemoveDialog}
                        title={t('dialogs.removeEmployeeDetail.title')}
                    />
                    <ConfirmDialog
                        cancelProps={{
                            onClick: () => setSelectedInvitationForResend(null),
                        }}
                        confirmProps={{
                            onClick: () => handleResendClick(),
                        }}
                        open={!!selectedInvitationForResend}
                        title={t('dialogs.resendInvite.title')}
                    />
                    <ConfirmDialog
                        cancelProps={{
                            onClick: () =>
                                setSelectedInvitationForCancellation(null),
                        }}
                        confirmProps={{
                            onClick: () => handleCancelClick(),
                        }}
                        open={!!selectedInvitationForCancellation}
                        title={t('dialogs.cancelInvite.title')}
                    />
                </>
            )}
        </BasePage>
    );
};

export const EmployeeDetailPage = styled(BaseEmployeeDetailPage)`
    .arrowIcon {
        width: 20px;
        transform: rotate(180deg);
    }
    .backButton {
        display: flex;
        justify-content: space-between;
        padding: 0 40px;
        max-width: 300px;

        svg {
            margin-right: ${({ theme }) => `${theme.spacing(2)}px`};
        }
    }
    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['md']}px`}) {
        ${InvitationForm}, .personalDetails {
            height: 100%;
            margin: 0;
        }
    }
`;
